import { type BrandDropDownViewModel, useGetApiV1BannerBrands } from '@/shared/api/generated';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

/** Marca disponível para filtrar turmas. */
export interface BannerBrandOption {
  readonly id: number;
  readonly name: string;
}

/**
 * Hook com as marcas disponíveis no filtro de turmas do Banner.
 *
 * Consome `GET /api/v1/banner/brands` — antes as marcas eram constantes locais
 * porque o backend não as expunha, e por isso os chips ficavam desabilitados.
 *
 * @returns `{ brands, isLoading, isError, refetch }`.
 *
 * @example
 * ```tsx
 * const { brands } = useBannerBrands();
 * ```
 */
export function useBannerBrands() {
  const query = useGetApiV1BannerBrands();

  const brands: BannerBrandOption[] = unwrapPagedResponse<BrandDropDownViewModel>(query.data)
    .filter(brand => brand.id !== undefined)
    .map(brand => ({ id: brand.id as number, name: brand.name ?? '' }));

  return {
    brands,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  } as const;
}
